/**
 * Sound effects.
 *
 * Tones are synthesised with the Web Audio API rather than shipped as files:
 * the whole set is a few hundred bytes of code instead of a few hundred
 * kilobytes of audio, and a Mini App is opened over mobile data.
 *
 * The context is created on the first user gesture, because mobile browsers
 * refuse to start audio before one.
 */

type SoundName =
  | 'place'
  | 'capture'
  | 'win'
  | 'lose'
  | 'draw'
  | 'select'
  | 'error'
  | 'notify'
  | 'hit'
  | 'miss'
  | 'sink';

interface Tone {
  /** Frequency in Hz, or a short melody. */
  notes: number[];
  /** Seconds per note. */
  step: number;
  type: OscillatorType;
  gain: number;
}

const TONES: Record<SoundName, Tone> = {
  place: { notes: [660], step: 0.06, type: 'sine', gain: 0.18 },
  select: { notes: [880], step: 0.04, type: 'sine', gain: 0.12 },
  capture: { notes: [330, 220], step: 0.07, type: 'triangle', gain: 0.2 },
  hit: { notes: [180, 140], step: 0.09, type: 'sawtooth', gain: 0.18 },
  miss: { notes: [300], step: 0.12, type: 'sine', gain: 0.12 },
  sink: { notes: [200, 160, 120, 90], step: 0.09, type: 'sawtooth', gain: 0.2 },
  win: { notes: [523, 659, 784, 1047], step: 0.11, type: 'sine', gain: 0.22 },
  lose: { notes: [392, 330, 262], step: 0.16, type: 'sine', gain: 0.2 },
  draw: { notes: [440, 440], step: 0.14, type: 'sine', gain: 0.16 },
  error: { notes: [160], step: 0.16, type: 'square', gain: 0.14 },
  notify: { notes: [784, 1047], step: 0.08, type: 'sine', gain: 0.18 },
};

let context: AudioContext | null = null;
let enabled = true;

export function setSoundEnabled(value: boolean): void {
  enabled = value;
}

/** Call from a click handler so mobile browsers allow audio afterwards. */
export function unlockAudio(): void {
  if (context) {
    void context.resume();
    return;
  }
  try {
    context = new AudioContext();
  } catch {
    context = null;
  }
}

export function play(name: SoundName): void {
  if (!enabled) return;
  if (!context) unlockAudio();
  if (!context || context.state === 'suspended') {
    void context?.resume();
  }
  if (!context) return;

  const tone = TONES[name];
  const now = context.currentTime;

  tone.notes.forEach((frequency, index) => {
    const oscillator = context!.createOscillator();
    const gain = context!.createGain();

    oscillator.type = tone.type;
    oscillator.frequency.value = frequency;

    const start = now + index * tone.step;
    const end = start + tone.step;

    // A short ramp on both ends; a raw square edge clicks audibly.
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(tone.gain, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    oscillator.connect(gain).connect(context!.destination);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  });
}

/** Maps an engine sound event onto one of ours, ignoring unknown names. */
export function playEngineSound(name: string): void {
  if (name in TONES) play(name as SoundName);
}
